import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfReaderProps = {
  fileUrl: string | null;
  onLastPage?: () => void;
};

export const PdfReader = ({ fileUrl, onLastPage }: PdfReaderProps) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const lastPageNotifiedRef = useRef(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isLastPage = numPages ? currentPage === numPages : false;
  const containerHeight = window.innerHeight - 250;

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  };

  useEffect(() => {
    setNumPages(null);
    setCurrentPage(1);
    lastPageNotifiedRef.current = false;
  }, [fileUrl]);

  useEffect(() => {
    if (!numPages) return;

    const visiblePages = new Set<number>();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const pageNumber = parseInt(
            entry.target.getAttribute('data-page-number') || '1'
          );

          if (entry.isIntersecting) {
            visiblePages.add(pageNumber);
          } else {
            visiblePages.delete(pageNumber);
          }
        });

        if (visiblePages.size > 0) {
          const firstVisiblePage = Math.min(...Array.from(visiblePages));
          setCurrentPage(firstVisiblePage);
        }
      },
      {
        root: containerRef.current,
        threshold: 0.5,
      }
    );

    pageRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      pageRefs.current.forEach((ref) => {
        if (ref) observer.unobserve(ref);
      });
    };
  }, [numPages]);

  useEffect(() => {
    if (!numPages || !onLastPage) return;
    if (isLastPage && !lastPageNotifiedRef.current) {
      lastPageNotifiedRef.current = true;
      onLastPage();
    }
  }, [isLastPage, numPages, onLastPage]);

  return (
    <Document
      file={fileUrl}
      onLoadSuccess={onDocumentLoadSuccess}
      noData={<CircularProgress />}
      loading={<CircularProgress />}
    >
      {numPages && (
        <Box
          ref={containerRef}
          sx={{
            height: containerHeight,
            overflowY: 'auto',
            overflowX: 'auto',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            p: 2,
          }}
        >
          {Array.from(new Array(numPages), (_, index) => (
            <Box
              key={`page_${index + 1}`}
              ref={(el: HTMLDivElement | null) => {
                pageRefs.current[index] = el;
              }}
              data-page-number={index + 1}
              sx={{
                display: 'inline-block',
                width: 'max-content',
              }}
            >
              <Page
                pageNumber={index + 1}
                renderTextLayer={false}
                renderAnnotationLayer={false}
              />
            </Box>
          ))}
        </Box>
      )}
    </Document>
  );
};
